import { ApplicationRef, ComponentRef, createComponent, EmbeddedViewRef, EnvironmentInjector, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';
import { WhiteboardPopupComponent } from '../components/whiteboard/whiteboard-popup.component';
import { CanvasData, WhiteboardComponent } from '../components/whiteboard/whiteboard.component';





@Injectable({
  providedIn: 'root'
})
export class WhiteboardService {
  private componentRef?: ComponentRef<WhiteboardPopupComponent>;
  private isDirty = false;
  
  // Observable per lo stato della whiteboard
  private isOpen$ = new BehaviorSubject<boolean>(false);
  private whiteboardClosed$ = new Subject<boolean>(); // true = salvato, false = annullato
  
  constructor(
    private applicationRef: ApplicationRef,
    private injector: EnvironmentInjector
  ) {}

  /**
   * Verifica se la whiteboard è aperta
   */
  get isOpen(): boolean {
    return this.isOpen$.value;
  }

  /**
   * Observable per monitorare lo stato aperto/chiuso
   */
  get openState$(): Observable<boolean> {
    return this.isOpen$.pipe(distinctUntilChanged());
  }

  /**
   * Observable per gli eventi di chiusura
   */
  get onClose$(): Observable<boolean> {
    return this.whiteboardClosed$.asObservable();
  }

  /**
   * Apre la whiteboard
   * @param canvasData Configurazione della whiteboard
   * @returns true se l'operazione è riuscita
   */
  async open(canvasData: CanvasData): Promise<boolean> {
    // Se è già aperta, chiudila prima
    if (this.componentRef) {
      console.error('WhiteboardService: Cannot open a new whiteboard while one is already open.');
      this.close(true);
    }
    console.log(`WhiteboardService: Opening whiteboard with data:`, canvasData);

    // Crea sempre una nuova istanza
    this.createComponent(canvasData);
    
    this.isDirty = false;
    return true;
  }

  /**
   * Chiude la whiteboard
   * @param saved Indica se le modifiche sono state salvate
   */
  close(saved: boolean = false): void {
    if (!this.componentRef) return;
    
    console.log(`[WhiteboardService] Closing whiteboard, saved: ${saved}`);
    // Rimuovi dal DOM
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    domElem.remove();
    
    // Detacha la vista dall'applicazione
    this.applicationRef.detachView(this.componentRef.hostView);
    
    // Distruggi il componente
    this.componentRef.destroy();
    
    // Reset stato
    this.componentRef = undefined;
    this.isDirty = false;
    setTimeout(() => {
    this.isOpen$.next(false);
    
    // Emetti evento di chiusura
    this.whiteboardClosed$.next(saved);
    }, 0);
  }


  /**
   * Marca la whiteboard come modificata
   */
  markAsDirty(): void {
    this.isDirty = true;
  }

  /**
   * Verifica se ci sono modifiche non salvate
   */
  hasUnsavedChanges(): boolean {
    return this.isDirty;
  }


  /**
   * Ottiene il riferimento al componente whiteboard interno
   */
  getWhiteboardComponent(): WhiteboardComponent | undefined {
    return this.componentRef?.instance.whiteboardComponent;
  }

  private createComponent(canvasData: CanvasData): void {
    // console.log('[WhiteboardService] Creating new component with image:', canvasData?.image?.substring(0, 50) + '...');
    
    // Crea il componente usando l'API moderna di Angular
    this.componentRef = createComponent(WhiteboardPopupComponent, {
      environmentInjector: this.injector
    });
    
    // IMPORTANTE: Aggiungi un piccolo delay per assicurarti che il DOM sia pulito
    // Questo potrebbe risolvere il problema del "flash" della vecchia immagine
    requestAnimationFrame(() => {
      if (!this.componentRef) return;
      
      // Configura le proprietà DOPO che il DOM è stato creato
      this.componentRef.instance.canvasData = canvasData;
      
      // Sottoscrivi all'evento di chiusura
      this.componentRef.instance.close.subscribe((saved: boolean) => {
        this.close(saved);
      });
      this.componentRef.instance.opened.subscribe(() => {
        this.isOpen$.next(true);
      });
      
      // Forza il change detection
      this.componentRef.changeDetectorRef.detectChanges();
    });
    
    // Aggiungi alla vista dell'applicazione
    this.applicationRef.attachView(this.componentRef.hostView);
    
    // Aggiungi al DOM
    const domElem = (this.componentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;
    document.body.appendChild(domElem);
    
    // Il FloatingPanelComponent gestirà automaticamente posizione e dimensioni
    
    // Aggiungi listener per tracciare le modifiche
    this.setupDirtyTracking();
    
    
  }

  private setupDirtyTracking(): void {
    // Qui dovresti aggiungere la logica per tracciare le modifiche
    // Per esempio, ascoltando gli eventi dal WhiteboardComponent
    // Questa è una implementazione semplificata
    setTimeout(() => {
      if (this.componentRef?.instance.whiteboardComponent) {
        // Assumendo che il WhiteboardComponent emetta eventi quando viene modificato
        // Dovrai adattarlo alla tua implementazione reale
        const whiteboardComp = this.componentRef.instance.whiteboardComponent;
        
        // Esempio: se c'è un evento 'change' o 'draw'
        // whiteboardComp.onChange?.subscribe(() => this.markAsDirty());
      }
    }, 100);
  }
}